import { defineMutation, patch } from '@rocketlane/state-db';
import { mirrorPhaseEntityOrder, phaseCollection } from 'collections/phase';
import { getProjectFromCollection, projectCollection } from 'collections/project';
import { converToEntityId, PhaseEntityType } from 'data/projects/phase-entity';
import { broadcastPlanLaneRefresh } from '../plan-board/tab-sync';
import { taskCollection } from './task-collection';
/**
 * Patch both phases' `entityOrder` — in `projectCollection` and on the phase
 * bean, its two homes since the phase state-db migration.
 *
 * The project-plan swimlane renders a phase's cards from
 * `projectPhase.entityOrder` — a `Project`-level array of `"TASK_<id>"` keys —
 * not from the task list. That array only refreshes on a full project refetch,
 * so a moved task is reactively correct in `taskCollection` (it disappears
 * from the old swimlane) but invisible in the new one until the round-trip
 * lands.
 *
 * Note `source: 'local'` on both the patch and its undo. This is a
 * client-computed guess about ordering the server owns; broadcasting it would
 * hand peers a value that a rollback here can no longer retract there. Peers
 * converge through the lane-refresh signals instead.
 */ const patchEntityOrder = (taskId, { projectId, fromPhaseId }, toPhaseId)=>patch('project.entityOrder', ()=>{
        const project = projectId == null ? undefined : getProjectFromCollection(projectId);
        if (!(project === null || project === void 0 ? void 0 : project.projectPhases)) {
            return ()=>undefined;
        }
        const entityId = converToEntityId(taskId, PhaseEntityType.Task);
        const changedPhases = [];
        const nextPhases = project.projectPhases.map((phase)=>{
            var _phase_entityOrder, _phase_entityOrder1;
            if (phase.projectPhaseId === fromPhaseId && ((_phase_entityOrder = phase.entityOrder) === null || _phase_entityOrder === void 0 ? void 0 : _phase_entityOrder.includes(entityId))) {
                const next = {
                    ...phase,
                    entityOrder: phase.entityOrder.filter((e)=>e !== entityId)
                };
                changedPhases.push(next);
                return next;
            }
            if (phase.projectPhaseId === toPhaseId && !((_phase_entityOrder1 = phase.entityOrder) === null || _phase_entityOrder1 === void 0 ? void 0 : _phase_entityOrder1.includes(entityId))) {
                var _phase_entityOrder2;
                const next = {
                    ...phase,
                    entityOrder: [
                        ...(_phase_entityOrder2 = phase.entityOrder) !== null && _phase_entityOrder2 !== void 0 ? _phase_entityOrder2 : [],
                        entityId
                    ]
                };
                changedPhases.push(next);
                return next;
            }
            return phase;
        });
        if (changedPhases.length === 0) {
            return ()=>undefined;
        }
        projectCollection.push([
            {
                ...project,
                projectPhases: nextPhases
            }
        ], {
            source: 'local'
        });
        // `entityOrder`'s other home. The rearrange tree reads the phase bean, so
        // patching only the project above leaves it ordering by the pre-move state.
        const undoBeans = mirrorPhaseEntityOrder(changedPhases, 'local');
        // Restores the exact pre-patch project, captured above. The authoritative
        // order still arrives with the next project refetch either way; this is
        // what keeps the swimlane honest in between.
        return ()=>{
            projectCollection.push([
                project
            ], {
                source: 'local'
            });
            undoBeans();
        };
    });
/**
 * Move a task to another phase.
 *
 * `taskCollection.update` routes a `projectPhase` patch to `TaskAPI.updatePhase`
 * internally (see `routeUpdate`), so the overlay applies instantly and every
 * collection-backed view re-files the task immediately.
 *
 * The entity write reaches peers on its own, through `attachTabSync`. What it
 * cannot carry is the two surfaces that render a phase's membership from
 * something else — the swimlane's `entityOrder` (patched locally above) and
 * the paginated board's lane page tokens. Peers hold their own copies of both,
 * so each needs an explicit nudge to re-read. That is what the signals are,
 * and forgetting them is exactly how a cross-phase move came to sync
 * everywhere except the board it was dragged on.
 */ export const moveTaskToPhase = defineMutation({
    name: 'task.moveToPhase',
    prepare: ({ taskId })=>{
        var _existing_projectPhase, _existing_project;
        const existing = taskCollection.get(taskId);
        return {
            fromPhaseId: existing === null || existing === void 0 ? void 0 : (_existing_projectPhase = existing.projectPhase) === null || _existing_projectPhase === void 0 ? void 0 : _existing_projectPhase.projectPhaseId,
            projectId: existing === null || existing === void 0 ? void 0 : (_existing_project = existing.project) === null || _existing_project === void 0 ? void 0 : _existing_project.projectId
        };
    },
    optimistic: {
        entity: ({ taskId, payload })=>taskCollection.update(taskId, payload),
        companions: ({ taskId, payload }, context)=>{
            var _payload_projectPhase;
            const toPhaseId = (_payload_projectPhase = payload.projectPhase) === null || _payload_projectPhase === void 0 ? void 0 : _payload_projectPhase.projectPhaseId;
            // A no-op "move" to the phase the task already sits in patches nothing,
            // so there is nothing to undo either.
            if (toPhaseId == null || toPhaseId === context.fromPhaseId) {
                return [];
            }
            return [
                patchEntityOrder(taskId, context, toPhaseId)
            ];
        },
        reconcile: 'server-response',
        result: ({ taskId })=>taskCollection.get(taskId)
    },
    sync: {
        // Thunk, not a literal: this module sits in a cycle
        // (tasks → mutations → plan-board → tasks), so reading `projectCollection`
        // at import time can catch that module mid-evaluation.
        // `phaseCollection` is in here because the companion patch writes
        // `entityOrder`'s second home; an undeclared write is reported, and rightly.
        entities: ()=>[
                taskCollection,
                projectCollection,
                phaseCollection
            ],
        signals: ({ payload }, { fromPhaseId, projectId })=>{
            var _payload_projectPhase;
            const toPhaseId = (_payload_projectPhase = payload.projectPhase) === null || _payload_projectPhase === void 0 ? void 0 : _payload_projectPhase.projectPhaseId;
            if (projectId == null || toPhaseId == null || toPhaseId === fromPhaseId) {
                return [];
            }
            // Both lanes: the source loses a card, the destination gains one. A peer
            // showing only one of them still needs the one it is showing.
            return [
                fromPhaseId,
                toPhaseId
            ].filter((phaseId)=>phaseId != null).map((phaseId)=>({
                    name: `plan-lane:${projectId}:${phaseId}`,
                    send: ()=>broadcastPlanLaneRefresh(projectId, phaseId)
                }));
        }
    }
});
